import { createRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { AuthLayout } from "@/components/auth/AuthLayout";
import LoadingSpinner from "@/components/loading-spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { authClient } from "@/lib/auth";
import { useMemberships } from "@/queries/user-queries";
import { loginRoute } from "./loginRoute";
import { rootRoute } from "./rootRoute";

// better-auth 1.7 redirects here with the *signed* OAuth request query rather
// than a one-shot `consent_code`. We only read a couple of params for display;
// the whole query string is handed back verbatim as `oauth_query` so the server
// can verify its signature.
const consentSearchSchema = z
    .object({
        client_id: z.string(),
        scope: z.string().optional(),
        sig: z.string(),
    })
    .passthrough();

export const oauthConsentRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/oauth/consent",
    validateSearch: consentSearchSchema,
    beforeLoad: async ({ search }) => {
        const session = await authClient.getSession();
        if (!session.data?.user) {
            // Bounce back through login carrying the OAuth params so the flow
            // resumes at /oauth2/authorize after sign-in.
            throw redirect({
                to: loginRoute.to,
                search: { ...search, redirect: window.location.href },
            });
        }
    },
    component: ConsentScreen,
});

function ConsentScreen() {
    const search = oauthConsentRoute.useSearch();
    const memberships = useMemberships();
    const [organizationId, setOrganizationId] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState<"accept" | "deny" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [clientName, setClientName] = useState<string | null>(null);

    useEffect(() => {
        // Only auto-pick when there is exactly one team — multi-team users
        // must explicitly choose so they don't grant access to the wrong one.
        if (!organizationId && memberships.data?.length === 1) {
            setOrganizationId(memberships.data[0]!.organizationId);
        }
    }, [memberships.data, organizationId]);

    useEffect(() => {
        const authBase = import.meta.env.VITE_AUTH_BASE_URL || window.location.origin;
        let cancelled = false;
        // 1.7 renamed /oauth2/client/:id to /oauth2/public-client and returns
        // RFC 7591 names, so the display name is `client_name`.
        fetch(`${authBase}/api/auth/oauth2/public-client?client_id=${encodeURIComponent(search.client_id)}`, {
            credentials: "include",
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((data: { client_name?: string } | null) => {
                if (!cancelled && data?.client_name) setClientName(data.client_name);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [search.client_id]);

    const submit = async (accept: boolean) => {
        setSubmitting(accept ? "accept" : "deny");
        setError(null);
        try {
            const authBase = import.meta.env.VITE_AUTH_BASE_URL || window.location.origin;
            const res = await fetch(`${authBase}/api/oauth/consent`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    // The signed query exactly as the provider handed it to us.
                    oauth_query: window.location.search.replace(/^\?/, ""),
                    accept,
                    organizationId: accept ? organizationId : undefined,
                }),
            });
            if (!res.ok) {
                const { error: msg } = (await res.json().catch(() => ({}))) as { error?: string };
                setError(msg || "Failed to record consent. Please try again.");
                setSubmitting(null);
                return;
            }
            const { redirect_uri } = (await res.json()) as { redirect_uri: string };
            window.location.href = redirect_uri;
        } catch (e) {
            console.error(e);
            setError("Network error. Please try again.");
            setSubmitting(null);
        }
    };

    if (memberships.isLoading) {
        return (
            <AuthLayout>
                <Card>
                    <CardContent className="flex justify-center py-10">
                        <LoadingSpinner />
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    const noOrgs = !memberships.data || memberships.data.length === 0;
    const scopes = search.scope?.split(" ").filter(Boolean) ?? [];

    return (
        <AuthLayout>
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">
                        <img src="/orsane.svg" alt="Orsane" className="w-auto h-4 mx-auto mb-2" />
                    </CardTitle>
                    <CardDescription className="text-neutral-600">
                        <span className="font-medium">{clientName ?? search.client_id}</span> wants to access your
                        Orsane account.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-6">
                        {scopes.length > 0 && (
                            <div className="text-sm text-neutral-600">
                                <div className="font-medium text-neutral-900 mb-2">Permissions</div>
                                <ul className="list-disc list-inside space-y-1">
                                    {scopes.map((s) => (
                                        <li key={s}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {noOrgs ? (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                                You need to belong to a team before granting access. Create or join a team first.
                            </div>
                        ) : memberships.data!.length === 1 ? (
                            <div className="text-sm text-neutral-600">
                                Granting access for team{" "}
                                <span className="font-medium text-neutral-900">
                                    {memberships.data![0]!.organizationName}
                                </span>
                                .
                            </div>
                        ) : (
                            <FieldGroup className="gap-4">
                                <Field>
                                    <FieldLabel>Grant access for team</FieldLabel>
                                    <Select value={organizationId} onValueChange={setOrganizationId}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a team">
                                                {(v: string) =>
                                                    memberships.data!.find((m) => m.organizationId === v)
                                                        ?.organizationName ?? "Select a team"
                                                }
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                {memberships.data!.map((m) => (
                                                    <SelectItem key={m.organizationId} value={m.organizationId}>
                                                        {m.organizationName}
                                                    </SelectItem>
                                                ))}
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </FieldGroup>
                        )}

                        {error && (
                            <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                                {error}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => submit(false)}
                                disabled={submitting !== null}
                            >
                                {submitting === "deny" ? "Denying..." : "Deny"}
                            </Button>
                            <Button
                                type="button"
                                className="flex-1"
                                onClick={() => submit(true)}
                                disabled={submitting !== null || noOrgs || !organizationId}
                            >
                                {submitting === "accept" ? "Allowing..." : "Allow"}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </AuthLayout>
    );
}
